import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Send,
  Megaphone,
  MessageSquare,
  Briefcase,
  Mail,
  Linkedin,
  Reply,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from "lucide-react";
import type { ContactCampaign, ContactOpportunity } from "@/hooks/useContactIntelligence";
import type { ContactOutreachMessage } from "@/hooks/useContactDetail";

// ── Props ──────────────────────────────────────────────────────────

interface ContactCampaignsTabProps {
  campaigns: ContactCampaign[] | undefined;
  opportunities: ContactOpportunity[] | undefined;
  outreachMessages: ContactOutreachMessage[] | undefined;
}

// ── Helpers ────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-muted text-muted-foreground",
  enrolled: "bg-blue-500/10 text-blue-700 border-blue-500/20",
  contacted: "bg-indigo-500/10 text-indigo-700 border-indigo-500/20",
  connected: "bg-green-500/10 text-green-700 border-green-500/20",
  replied: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20",
  meeting_booked: "bg-purple-500/10 text-purple-700 border-purple-500/20",
  pushed: "bg-indigo-500/10 text-indigo-700 border-indigo-500/20",
  bounced: "bg-red-500/10 text-red-700 border-red-500/20",
  opted_out: "bg-red-500/10 text-red-700 border-red-500/20",
};

const MESSAGE_STATUS_ICONS: Record<string, typeof Send> = {
  draft: Clock,
  ready_to_send: CheckCircle2,
  pushed: Send,
  sent: Send,
  failed: XCircle,
  bounced: XCircle,
  replied: Reply,
};

const MESSAGE_TYPE_ICONS: Record<string, typeof Send> = {
  linkedin_connection_request: Linkedin,
  linkedin_inmail: Mail,
  linkedin_connection_followup: MessageSquare,
  email_cold: Send,
  email_followup: Reply,
};

function formatDate(dateStr: string | null) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatShortDate(dateStr: string | null) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

// ── Component ──────────────────────────────────────────────────────

export function ContactCampaignsTab({
  campaigns,
  opportunities,
  outreachMessages,
}: ContactCampaignsTabProps) {
  const campaignCount = campaigns?.length || 0;
  const messageCount = outreachMessages?.length || 0;
  const oppCount = opportunities?.length || 0;

  // Split messages into campaign-linked and orphan
  const orphanMessages = outreachMessages?.filter((m) => !m.campaign_id) || [];

  const hasAnyData = campaignCount > 0 || messageCount > 0 || oppCount > 0;

  if (!hasAnyData) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <Send className="h-10 w-10 mb-3 opacity-30" />
        <p className="text-sm font-medium">No campaign history</p>
        <p className="text-xs mt-1">
          Campaign enrollments, outreach messages, and opportunities will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-1">
      {/* ── Summary Bar ────────────────────────────────────────── */}
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center border rounded-md p-2">
          <div className="text-lg font-bold">{campaignCount}</div>
          <div className="text-[10px] text-muted-foreground">Campaigns</div>
        </div>
        <div className="text-center border rounded-md p-2">
          <div className="text-lg font-bold">{messageCount}</div>
          <div className="text-[10px] text-muted-foreground">Messages</div>
        </div>
        <div className="text-center border rounded-md p-2">
          <div className="text-lg font-bold">{oppCount}</div>
          <div className="text-[10px] text-muted-foreground">Opportunities</div>
        </div>
      </div>

      <Separator />

      {/* ── Campaign History ───────────────────────────────────── */}
      {campaignCount > 0 && (
        <section>
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
            <Megaphone className="h-3.5 w-3.5" />
            Campaigns ({campaignCount})
          </h3>
          <div className="space-y-2">
            {campaigns!.map((camp) => {
              // Find outreach messages for this campaign
              const campMessages = outreachMessages?.filter(
                (m) => m.campaign_id === camp.campaign_id
              ) || [];

              return (
                <div key={camp.id} className="border rounded-md p-3 space-y-2">
                  {/* Campaign Header */}
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium">{camp.campaign_name}</span>
                      {camp.enrolled_at && (
                        <span className="text-[10px] text-muted-foreground ml-2">
                          Enrolled {formatShortDate(camp.enrolled_at)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {camp.meeting_booked && (
                        <Badge className="text-[10px] h-4 px-1 bg-green-600">Meeting</Badge>
                      )}
                      {camp.outcome && (
                        <Badge variant="outline" className="text-[10px] h-4 px-1">
                          {camp.outcome}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Engagement Status + Timeline Dots */}
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${STATUS_COLORS[camp.engagement_status] || ""}`}
                    >
                      {camp.engagement_status?.replace(/_/g, " ") || "pending"}
                    </Badge>
                    {/* Timeline dots */}
                    <div className="flex items-center gap-1">
                      {camp.sent_at && (
                        <span className="text-[10px] text-muted-foreground" title={`Sent ${formatDate(camp.sent_at)}`}>
                          📤 {formatShortDate(camp.sent_at)}
                        </span>
                      )}
                      {camp.accepted_at && (
                        <span className="text-[10px] text-muted-foreground" title={`Accepted ${formatDate(camp.accepted_at)}`}>
                          ✅ {formatShortDate(camp.accepted_at)}
                        </span>
                      )}
                      {camp.replied_at && (
                        <span className="text-[10px] text-muted-foreground" title={`Replied ${formatDate(camp.replied_at)}`}>
                          💬 {formatShortDate(camp.replied_at)}
                        </span>
                      )}
                    </div>
                    {camp.reply_sentiment && (
                      <Badge variant="secondary" className="text-[10px] h-4 px-1">
                        {camp.reply_sentiment}
                      </Badge>
                    )}
                  </div>

                  {/* Campaign Outreach Messages */}
                  {campMessages.length > 0 && (
                    <div className="mt-1 space-y-1.5 pl-2 border-l-2 border-muted">
                      {campMessages.map((msg) => (
                        <OutreachMessageCard key={msg.id} message={msg} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Orphan Outreach Messages ──────────────────────────── */}
      {orphanMessages.length > 0 && (
        <>
          {campaignCount > 0 && <Separator />}
          <section>
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              <MessageSquare className="h-3.5 w-3.5" />
              Direct Outreach ({orphanMessages.length})
            </h3>
            <div className="space-y-1.5">
              {orphanMessages.map((msg) => (
                <OutreachMessageCard key={msg.id} message={msg} />
              ))}
            </div>
          </section>
        </>
      )}

      {/* ── Opportunities ─────────────────────────────────────── */}
      {oppCount > 0 && (
        <>
          <Separator />
          <section>
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              <Briefcase className="h-3.5 w-3.5" />
              Opportunities ({oppCount})
            </h3>
            <div className="space-y-1.5">
              {opportunities!.map((opp) => (
                <div
                  key={opp.id}
                  className="flex items-center justify-between text-sm border rounded-md p-2"
                >
                  <div>
                    <div className="font-medium text-xs">{opp.opportunity_name}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {[opp.buying_role, opp.relationship_strength]
                        .filter(Boolean)
                        .join(" · ") || "Associated"}
                    </div>
                  </div>
                  {opp.is_primary && (
                    <Badge variant="default" className="text-[10px] h-4 px-1">
                      Primary
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

// ── Outreach Message Card ──────────────────────────────────────────

function OutreachMessageCard({ message }: { message: ContactOutreachMessage }) {
  const TypeIcon = MESSAGE_TYPE_ICONS[message.message_type || ""] || Send;
  const StatusIcon = MESSAGE_STATUS_ICONS[message.status || ""] || AlertCircle;
  const displayText = message.final_text || message.generated_text;

  return (
    <div className="border rounded-md p-2.5 space-y-1.5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <TypeIcon className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs font-medium capitalize">
            {message.message_type?.replace(/_/g, " ") || "Message"}
          </span>
          {message.messaging_angle && (
            <Badge variant="secondary" className="text-[10px] h-4 px-1">
              {message.messaging_angle}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {message.status && (
            <Badge
              variant="outline"
              className={`text-[10px] h-4 px-1 ${
                message.status === "sent"
                  ? "bg-green-500/10 text-green-700 border-green-500/20"
                  : message.status === "draft"
                  ? "bg-muted text-muted-foreground"
                  : message.status === "failed"
                  ? "bg-red-500/10 text-red-700 border-red-500/20"
                  : ""
              }`}
            >
              <StatusIcon className="h-2.5 w-2.5 mr-0.5" />
              {message.status}
            </Badge>
          )}
        </div>
      </div>

      {/* Message Preview */}
      {displayText && (
        <p className="text-xs text-muted-foreground line-clamp-3">{displayText}</p>
      )}

      {/* Footer */}
      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
        {message.sender_name && <span>by {message.sender_name}</span>}
        {message.sent_at && <span>· Sent {formatDate(message.sent_at)}</span>}
        {!message.sent_at && message.created_at && (
          <span>· Created {formatDate(message.created_at)}</span>
        )}
      </div>

      {/* Reply */}
      {message.reply_text && (
        <div className="mt-1 p-2 bg-muted/50 rounded text-xs">
          <div className="flex items-center gap-1 mb-1">
            <Reply className="h-3 w-3 text-primary" />
            <span className="font-medium text-primary">Reply</span>
            {message.reply_sentiment && (
              <Badge variant="outline" className="text-[10px] h-4 px-1 ml-1">
                {message.reply_sentiment}
              </Badge>
            )}
            {message.reply_received_at && (
              <span className="text-[10px] text-muted-foreground ml-auto">
                {formatDate(message.reply_received_at)}
              </span>
            )}
          </div>
          <p className="text-muted-foreground line-clamp-3">{message.reply_text}</p>
        </div>
      )}
    </div>
  );
}
